/**
 * MyCollections — staff-facing "Ready to collect" dashboard.
 * Mobile-first redesign: compact greeting card, segmented tabs
 * (Focus / All / Cleared) so the screen never feels heavy, single-line
 * customer rows with one-tap Call / WhatsApp / Log, and quiet
 * "on your list X wk" aging chips for stubborn stragglers.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Phone, MessageCircle, IndianRupee, Search, CheckCircle2, PartyPopper, Sparkles } from 'lucide-react';
import { api, formatINR } from '../lib/store';
import { logger } from '../lib/logger';
import { toast } from 'sonner';

// Gentle language — user asked us to avoid harsh words.
const ACTION_LABELS = {
  called:            { label: 'Called',            color: 'bg-[#556B2F]/10 text-[#556B2F]' },
  promised:          { label: 'Promised to pay',   color: 'bg-[#DAA520]/10 text-[#8B6914]' },
  partial_payment:   { label: 'Partial payment',   color: 'bg-[#2E7D32]/10 text-[#2E7D32]' },
  payment_received:  { label: 'Payment received',  color: 'bg-[#2E7D32]/15 text-[#2E7D32]' },
  visited:           { label: 'Visited',           color: 'bg-[#4A90A4]/10 text-[#1F4E79]' },
  cheque_requested:  { label: 'Cheque requested',  color: 'bg-[#8B4513]/10 text-[#8B4513]' },
  no_response:       { label: 'No response',       color: 'bg-[#8F9485]/15 text-[#5C6150]' },
  note:              { label: 'Note',              color: 'bg-[#8F9485]/10 text-[#5C6150]' },
};

// Polite WhatsApp template — never accusatory, always gives an out.
const buildWhatsAppUrl = (mobile, customerName, amount, staffName) => {
  const msg = `Hello ${customerName || 'Sir/Madam'}, this is ${staffName || 'HEDA Plywoods'}. Just a friendly reminder — a balance of ${formatINR(amount)} is pending on your account. Please let us know if there's anything we can help with. Thank you! 🙏`;
  const digits = (mobile || '').replace(/\D/g, '');
  const num = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
};

// "On your list X wk" — shown only from 2 weeks onward, never pushy.
const agingWeeks = (firstSeenAt) => {
  if (!firstSeenAt) return 0;
  const days = (Date.now() - new Date(firstSeenAt).getTime()) / 86400000;
  return days >= 14 ? Math.floor(days / 7) : 0;
};

function greetingSlot() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

export default function MyCollectionsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('focus'); // focus | all | cleared
  const [actionOpen, setActionOpen] = useState(null);

  const staffName = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').name || ''; } catch { return ''; }
  }, []);
  const firstName = (staffName || '').split(' ')[0];

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    api.getMyCollections()
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="p-4"><Skeleton className="h-64 w-full" /></div>;
  }
  if (error) {
    return (
      <div className="p-4" data-testid="collections-not-enrolled">
        <Card className="border-[#DAA520]/40 bg-[#FFFBF0]">
          <CardContent className="py-8 text-center space-y-2">
            <Sparkles className="w-8 h-8 mx-auto text-[#DAA520]" />
            <p className="font-semibold text-[#1A1C16]">Collections isn&apos;t enabled for your account.</p>
            <p className="text-sm text-[#5C6150]">
              Ask the admin to switch on Collections for you in User Management if you&apos;d like to track outstanding payments.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const rows = data?.rows || [];
  const focus = data?.focus || [];
  const actionsByKey = data?.actions_by_key || {};
  const weekCleared = data?.week_cleared || [];
  const weekClearedTotal = data?.week_cleared_total || 0;
  const manualLoggedTotal = data?.manual_logged_total || 0;
  const totalAmount = data?.total_amount || 0;
  const grandActioned = weekClearedTotal + manualLoggedTotal;
  const denom = grandActioned + totalAmount;
  const pct = denom > 0 ? Math.min(100, Math.round((grandActioned / denom) * 100)) : 0;

  const filtered = rows.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.customer_name || '').toLowerCase().includes(q)
        || (r.customer_mobile || '').includes(q);
  });

  const lastSnapDate = data?.last_snapshot_at
    ? new Date(data.last_snapshot_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
    : null;

  const TABS = [
    { id: 'focus', label: 'Focus' },
    { id: 'all', label: `All · ${rows.length}` },
    { id: 'cleared', label: `Cleared · ${weekCleared.length}` },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-3" data-testid="my-collections-page">
      {/* Compact greeting card */}
      <div className="rounded-2xl border border-[#DAA520]/40 bg-gradient-to-br from-[#FFFBF0] to-[#F9FAF7] p-4">
        <p className="text-[11px] uppercase tracking-wider text-[#8B6914] font-semibold">
          Good {greetingSlot()}, {firstName || 'there'} 👋
        </p>
        {rows.length === 0 ? (
          <p className="text-lg font-bold text-[#1A1C16] mt-1">You&apos;re all clear 🎉</p>
        ) : (
          <>
            <p className="text-2xl sm:text-3xl font-bold font-mono-data text-[#1A1C16] mt-1" data-testid="collections-total-amount">
              {formatINR(totalAmount)}
            </p>
            <p className="text-[11px] text-[#5C6150] mt-0.5">
              left to collect · {rows.length} customer{rows.length === 1 ? '' : 's'}
              {lastSnapDate ? ` · list updated ${lastSnapDate}` : ''}
            </p>
            {grandActioned > 0 && (
              <div className="mt-3 space-y-1" data-testid="collections-progress">
                <div className="h-1.5 rounded-full bg-[#E2E6D9] overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#2E7D32] to-[#DAA520]" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-[10px] text-[#2E7D32] font-medium">
                  {formatINR(grandActioned)} moved since last upload {pct > 0 ? `(${pct}%)` : ''} 👏
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {rows.length === 0 && weekCleared.length === 0 && (
        <Card className="border-[#E2E6D9]">
          <CardContent className="py-10 text-center text-sm text-[#5C6150]">
            No outstanding customers in your list. Enjoy the calm 🌿
          </CardContent>
        </Card>
      )}

      {(rows.length > 0 || weekCleared.length > 0) && (
        <>
          {/* Segmented tabs — one thing on screen at a time */}
          <div className="grid grid-cols-3 gap-1 p-1 bg-[#EDEFE6] rounded-xl sticky top-14 z-10" data-testid="collections-tabs">
            {TABS.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`py-2 rounded-lg text-[12px] font-semibold transition-colors ${
                  tab === t.id ? 'bg-white text-[#556B2F] shadow-sm' : 'text-[#5C6150]'
                }`}
                data-testid={`collections-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* FOCUS tab */}
          {tab === 'focus' && (
            <div className="space-y-2" data-testid="collections-focus">
              {focus.length > 0 && (
                <p className="text-[11px] text-[#8F9485] px-1">
                  Your top {focus.length} — start here, small wins compound ✨
                </p>
              )}
              {focus.map(r => (
                <CustomerRow
                  key={r.id}
                  row={r}
                  lastAction={actionsByKey[r.customer_mobile || r.customer_name]}
                  staffName={staffName}
                  onLog={() => setActionOpen(r)}
                  highlight
                />
              ))}
              {focus.length === 0 && (
                <p className="text-center text-sm text-[#8F9485] py-8">Nothing here — check the All tab 🌿</p>
              )}
            </div>
          )}

          {/* ALL tab */}
          {tab === 'all' && (
            <div className="space-y-2" data-testid="collections-full-list">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8F9485]" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name or mobile…"
                  className="h-10 pl-9 text-sm rounded-xl bg-white"
                  data-testid="collections-search-input"
                />
              </div>
              {filtered.map(r => (
                <CustomerRow
                  key={r.id}
                  row={r}
                  lastAction={actionsByKey[r.customer_mobile || r.customer_name]}
                  staffName={staffName}
                  onLog={() => setActionOpen(r)}
                />
              ))}
              {filtered.length === 0 && (
                <p className="text-center text-sm text-[#8F9485] py-8">
                  {search ? <>No customer matches &ldquo;{search}&rdquo;</> : 'Your list is empty — all clear 🌿'}
                </p>
              )}
            </div>
          )}

          {/* CLEARED tab */}
          {tab === 'cleared' && (
            <div data-testid="collections-week-cleared">
              {weekCleared.length === 0 ? (
                <p className="text-center text-sm text-[#8F9485] py-8">Nothing cleared since the last upload yet.</p>
              ) : (
                <Card className="border-[#2E7D32]/40 bg-[#F0F7EE]">
                  <CardContent className="py-3">
                    <p className="text-xs font-bold uppercase tracking-wide text-[#2E7D32] flex items-center gap-1.5 mb-2">
                      <PartyPopper className="w-4 h-4" /> Cleared since last upload — {formatINR(weekClearedTotal)} 👏
                    </p>
                    <div className="text-xs space-y-1">
                      {weekCleared.map((r, i) => (
                        <div key={i} className="flex items-center justify-between border-b border-[#E2E6D9] last:border-0 py-1.5">
                          <span className="text-[#1A1C16] truncate">{r.customer_name}</span>
                          <span className="font-mono-data text-[#2E7D32] font-semibold ml-2 whitespace-nowrap">{formatINR(r.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </>
      )}

      <ActionDialog
        row={actionOpen}
        snapshotId={data?.snapshot_id}
        onClose={() => setActionOpen(null)}
        onSaved={() => { setActionOpen(null); load(); }}
      />
    </div>
  );
}

// Compact single-card row: name + amount on line 1, chips + action
// icons on line 2. Thumb-sized 36px tap targets.
function CustomerRow({ row, lastAction, staffName, onLog, highlight }) {
  const waUrl = buildWhatsAppUrl(row.customer_mobile, row.customer_name, row.amount, staffName);
  const callHref = row.customer_mobile ? `tel:${row.customer_mobile}` : null;
  const meta = lastAction ? ACTION_LABELS[lastAction.action] : null;
  const weeks = agingWeeks(row.first_seen_at);
  return (
    <div
      className={`bg-white border rounded-xl px-3 py-2.5 ${highlight ? 'border-[#DAA520]/50 shadow-sm' : 'border-[#E2E6D9]'}`}
      data-testid={`customer-card-${row.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-[13px] text-[#1A1C16] truncate leading-tight">{row.customer_name}</p>
        <span className="font-mono-data font-bold text-[#556B2F] text-[13px] whitespace-nowrap">{formatINR(row.amount)}</span>
      </div>
      <div className="flex items-center justify-between gap-2 mt-1.5">
        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
          {weeks > 0 && (
            <span
              className="text-[10px] font-medium rounded-full px-2 py-0.5 bg-[#DAA520]/10 text-[#8B6914] whitespace-nowrap"
              data-testid={`aging-chip-${row.id}`}
            >
              on list {weeks} wk
            </span>
          )}
          {meta && (
            <span className={`text-[10px] font-medium rounded-full px-2 py-0.5 whitespace-nowrap ${meta.color}`}>
              {meta.label}
              {lastAction.amount ? ` · ${formatINR(lastAction.amount)}` : ''}
              {lastAction.promise_date ? ` · by ${lastAction.promise_date}` : ''}
            </span>
          )}
          {!meta && weeks === 0 && (
            <span className="text-[10px] text-[#8F9485] font-mono">{row.customer_mobile || 'no mobile'}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {callHref && (
            <a
              href={callHref}
              className="w-9 h-9 rounded-full border border-[#556B2F]/30 text-[#556B2F] flex items-center justify-center active:bg-[#556B2F] active:text-white"
              data-testid={`call-btn-${row.id}`}
              aria-label="Call"
            >
              <Phone className="w-4 h-4" />
            </a>
          )}
          {row.customer_mobile && (
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="w-9 h-9 rounded-full border border-[#25D366]/40 text-[#128C7E] flex items-center justify-center active:bg-[#25D366] active:text-white"
              data-testid={`whatsapp-btn-${row.id}`}
              aria-label="WhatsApp"
            >
              <MessageCircle className="w-4 h-4" />
            </a>
          )}
          <button
            type="button"
            onClick={onLog}
            className="w-9 h-9 rounded-full border border-[#DAA520]/40 text-[#8B6914] flex items-center justify-center active:bg-[#DAA520] active:text-white"
            data-testid={`log-action-btn-${row.id}`}
            aria-label="Log action"
          >
            <CheckCircle2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionDialog({ row, snapshotId, onClose, onSaved }) {
  const [action, setAction] = useState('called');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [promiseDate, setPromiseDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (row) { setAction('called'); setAmount(''); setNote(''); setPromiseDate(''); }
  }, [row]);

  if (!row) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.logCollectionAction({
        snapshot_id: snapshotId,
        customer_name: row.customer_name,
        customer_mobile: row.customer_mobile,
        action,
        amount: amount ? parseFloat(amount) : null,
        note,
        promise_date: promiseDate || null,
      });
      toast.success('Action logged.');
      onSaved();
    } catch (err) {
      logger.error(err);
      toast.error(err.response?.data?.detail || 'Failed to log action');
    } finally {
      setSaving(false);
    }
  };

  const showAmount = ['partial_payment', 'payment_received'].includes(action);
  const showDate = action === 'promised';

  return (
    <Dialog open={Boolean(row)} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="log-action-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Log action for {row.customer_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#8F9485] mb-1">What happened?</p>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger data-testid="log-action-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ACTION_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {showAmount && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#8F9485] mb-1">Amount received</p>
              <div className="relative">
                <IndianRupee className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-[#8F9485]" />
                <Input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-8"
                  placeholder={String(row.amount)}
                  data-testid="log-action-amount"
                />
              </div>
            </div>
          )}
          {showDate && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#8F9485] mb-1">Promised by</p>
              <Input
                type="date"
                value={promiseDate}
                onChange={(e) => setPromiseDate(e.target.value)}
                data-testid="log-action-promise-date"
              />
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#8F9485] mb-1">Note (optional)</p>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything the team should know…"
              rows={3}
              data-testid="log-action-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#556B2F] hover:bg-[#3F5222]" data-testid="log-action-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
